import { useEffect } from 'react'
import { useEnrichedBilling } from '@renderer/chat/hooks'
import { BillingRefreshNotice } from '@renderer/components/pricing/billing-refresh-notice'
import type { BillingPlanV2Tier } from '@highlight/common/types'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { getUpgradeTarget, isPlanEnded, v2TierFor } from '@renderer/chat/utils/billingUtils'
import { PlanEndedPaywall, PlanUpgradePaywall, TrialEndingPaywall } from '@renderer/components/pricing/plan-paywall'
import { TopUpPaywall } from '@renderer/components/pricing/top-up-paywall'
import { TOP_UP_SPECS, type TopUpKindSpec } from '@renderer/components/pricing/top-up-kinds'
import {
  usePaywallShown,
  usePlanRenewal,
  usePlanUpgrade,
  useTopUp,
  useTrialKeep,
} from '@renderer/components/pricing/use-top-up-actions'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { SidepanelPlanBanners } from './plan-banners/sidepanel-plan-banners'
import { SidepanelPlanChangeBanner } from './plan-banners/sidepanel-plan-change-banner'
import { useClearSidepanelPaywallRequest, useSidepanelPaywallRequest } from './sidepanel-paywall-request'
import { docksTrialEnding, resolveTopUpStrips, sellsPlan, type TrialEndingStrip } from './sidepanel-paywall-strips'

/** Both variants sit flush at the top of the home scroller, above Upcoming. */
const BANNER_CLASSES = 'border-b'

const SURFACE = 'sidepanel home'

/**
 * Docks the tier-themed subscription strip at the top of the sidepanel home
 * when a plan needs money to keep working: the plan lapsed entirely (Renew),
 * a card-less trial is about to end on free (Keep), its credit allowance is
 * spent, or its meeting hours are (Add). A strip also shows on request, for
 * the surfaces that used to open a paywall modal.
 *
 * Free plans map to no tier of their own and so can buy no top-up. When one
 * runs out of credits or hours, or a gate just refused it, they get the plan
 * promo for the tier they would upgrade to instead. Legacy plans hold no tier
 * either but are paid, and sellsPlan stands the promo down.
 *
 * Before the revamp flips, the plan-change announcement docks under whatever
 * strip is up. The strips want money now. The announcement can wait a scroll.
 */
export function SidepanelPlanPaywall() {
  if (usePricingEdition() === 'v2-revamp') return <SidepanelPlanBanners />
  return (
    <>
      <PreRevampPlanPaywall />
      <SidepanelPlanChangeBanner />
    </>
  )
}

function PreRevampPlanPaywall() {
  const paywall = usePaywallState()
  const { subscription, lastUpdated } = useUnifiedBilling()
  const request = useSidepanelPaywallRequest()
  const clearRequest = useClearSidepanelPaywallRequest()
  const tier = v2TierFor(paywall.plan)

  const { strips, staleRequest } = resolveTopUpStrips({
    paywall,
    subscription,
    request,
    billingUpdatedAt: lastUpdated,
  })

  useEffect(() => {
    if (staleRequest) {
      clearRequest(staleRequest)
    }
  }, [staleRequest, clearRequest])

  if (paywall.isLoading) return null
  if (!tier) {
    if (!sellsPlan(paywall) || strips.length === 0) return null
    return <PlanPromoBanner atLimit={strips.some((strip) => strip.atLimit)} />
  }
  // The window where the hydrated plan still names the tier before flipping to
  // free (which nulls the tier and unmounts this). `subscription.active` can't
  // gate it — it reads plan !== 'free', so it is never false while a tier
  // resolves. The dev billing simulation pins this state for previewing.
  if (isPlanEnded(subscription)) {
    return <PlanEndedBanner tier={tier} />
  }
  // A trial about to end on free has nothing worth topping up, so this outranks the top-up strips.
  const trialEnding = docksTrialEnding(paywall, subscription)
  if (trialEnding) {
    return <TrialEndingBanner {...trialEnding} />
  }
  return (
    <>
      {strips.map((strip) => (
        <TopUpBanner key={strip.kind} tier={tier} spec={TOP_UP_SPECS[strip.kind]} atLimit={strip.atLimit} />
      ))}
    </>
  )
}

function TopUpBanner({ tier, spec, atLimit }: { tier: BillingPlanV2Tier; spec: TopUpKindSpec; atLimit: boolean }) {
  const clearRequest = useClearSidepanelPaywallRequest()
  // The refresh clears the meter, not the request. Without this a requested
  // strip would outlive the top-up it just sold.
  const surface = { tier, from: SURFACE, onPurchased: () => clearRequest(spec.kind) }
  usePaywallShown(surface, spec.analytics.feature, atLimit ? 'limit-reached' : 'headroom')
  const { onPurchase, onUpgrade, isPending, isUpgradePending, errorMessage, needsBillingRefresh, clearBillingRefresh } =
    useTopUp(spec, surface)

  if (needsBillingRefresh) {
    return (
      <BillingRefreshNotice
        onRefreshed={() => {
          clearBillingRefresh()
          surface.onPurchased()
        }}
      />
    )
  }

  return (
    <TopUpPaywall
      spec={spec}
      plan={tier}
      atLimit={atLimit}
      onPurchase={onPurchase}
      onUpgrade={onUpgrade}
      isPending={isPending}
      isUpgradePending={isUpgradePending}
      errorMessage={errorMessage}
      className={BANNER_CLASSES}
    />
  )
}

/**
 * The upgrade pitch for a plan with nothing to top up, which sellsPlan narrows
 * to free. The target comes from the same routing every other upgrade CTA
 * uses, so a free user is sold the same tier here as anywhere else.
 */
function PlanPromoBanner({ atLimit }: { atLimit: boolean }) {
  const paywall = usePaywallState()
  const edition = usePricingEdition()
  const { billingProfile } = useUnifiedBilling()
  const target = getUpgradeTarget(paywall.plan, billingProfile?.previousPlan)
  const surface = { tier: target ?? 'pro2', from: SURFACE }
  usePaywallShown(surface, 'Subscription', atLimit ? 'limit-reached' : 'requires-plan')
  const { onUpgrade, hasPaymentMethod, isPending, errorMessage, needsBillingRefresh, clearBillingRefresh } =
    usePlanUpgrade(surface)

  // getUpgradeTarget only returns null for a paid v2 tier, which resolves a
  // tier and never reaches this banner.
  if (!target) return null
  if (needsBillingRefresh) return <BillingRefreshNotice onRefreshed={clearBillingRefresh} />
  return (
    <PlanUpgradePaywall
      plan={target}
      edition={edition}
      discount={billingProfile?.discount ?? null}
      onUpgrade={onUpgrade}
      hasPaymentMethod={hasPaymentMethod}
      isPending={isPending}
      errorMessage={errorMessage}
      className={BANNER_CLASSES}
    />
  )
}

function TrialEndingBanner({ tier, daysLeft }: TrialEndingStrip) {
  const surface = { tier, from: SURFACE }
  usePaywallShown(surface, 'Subscription', 'trial-ending')
  const { onKeep, isPending, errorMessage } = useTrialKeep(surface)
  const { billingProfile } = useUnifiedBilling()
  const edition = usePricingEdition()

  return (
    <TrialEndingPaywall
      plan={tier}
      edition={edition}
      discount={billingProfile?.discount ?? null}
      daysLeft={daysLeft}
      onKeep={onKeep}
      isPending={isPending}
      errorMessage={errorMessage}
      className={BANNER_CLASSES}
    />
  )
}

function PlanEndedBanner({ tier }: { tier: BillingPlanV2Tier }) {
  const surface = { tier, from: SURFACE }
  usePaywallShown(surface, 'Subscription', 'plan-ended')
  const { onRenew, isPending, errorMessage, needsBillingRefresh, clearBillingRefresh } = usePlanRenewal(surface)
  const { billingProfile } = useEnrichedBilling()
  const edition = usePricingEdition()

  if (needsBillingRefresh) return <BillingRefreshNotice onRefreshed={clearBillingRefresh} />

  return (
    <PlanEndedPaywall
      plan={tier}
      edition={edition}
      discount={billingProfile?.discount ?? null}
      onRenew={onRenew}
      isPending={isPending}
      errorMessage={errorMessage}
      className={BANNER_CLASSES}
    />
  )
}
